import React, { useEffect, useRef, useState } from "react";
import {
  styled,
  Tooltip,
  Typography,
  Box,
  Grid,
  Paper,
  IconButton,
  Button,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  // Add as AddIcon,
  InsertDriveFile as FileIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { colors } from "../../constants/colors";
import { ReactComponent as Additem } from "../../assets/icons/additem.svg";
import OptionsButtonCustom from "../option-button/OptionsButtonCustom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import moment from "moment";

// Types
interface UploadedFile {
  id: number;
  name: string;
  size: string;
  uploadDate: string;
  file: File;
  file_path?: string;
}

interface AccordionUploadProps {
  titleName: string;
  subTitleName?: string;
  required?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  enableButtonLeft?: boolean;
  renderButtonLeft?: React.ReactNode;
  onClickButtonLeft?: () => void;
  enableButtonRight?: boolean;
  renderButtonRight?: React.ReactNode;
  onClickButtonRight?: () => void;
  titleButtonRight?: string;
  colorHeader?: string;
  hoverTitle?: string;
  uploadedFiles: UploadedFile[];
  setUploadedFiles: (files: UploadedFile[]) => void;
  acceptedFileTypes?: string;
  maxFileSize?: number; // in MB
  multiple?: boolean;
  isView?: boolean;
}

// Styled Components
const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${colors.themeMainColor}`,
  borderRadius: "8px",
  overflow: "hidden",
}));

const AccordionSummary = styled(
  (props: AccordionSummaryProps & { bgColor?: string }) => (
    <MuiAccordionSummary
      expandIcon={
        <ExpandMoreIcon
          sx={{
            fontSize: "1.8rem",
            color: colors.white,
          }}
        />
      }
      {...props}
    />
  )
)(({ theme, bgColor }) => ({
  backgroundColor: bgColor || colors.themeMainColor,
  color: colors.white,
  flexDirection: "row",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(180deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginRight: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const FileListHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.grey[50],
  marginBottom: theme.spacing(1),
}));

const FileListItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[200]}`,
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(6, 2),
  color: theme.palette.grey[500],
}));

const HiddenFileInput = styled("input")({
  display: "none",
});

// Utility Functions
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes: string[] = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const validateFileSize = (file: File, maxSizeMB: number): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

// Main Component
const AccordionUpload: React.FC<AccordionUploadProps> = ({
  titleName,
  subTitleName,
  required = false,
  defaultExpanded = false,
  expanded,
  enableButtonLeft = false,
  renderButtonLeft,
  onClickButtonLeft,
  enableButtonRight = false,
  renderButtonRight,
  onClickButtonRight,
  titleButtonRight = "เพิ่มไฟล์",
  colorHeader,
  hoverTitle,
  uploadedFiles,
  setUploadedFiles,
  acceptedFileTypes = "*",
  maxFileSize = 10, // 10MB default
  multiple = true,
  isView = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const base_url = useSelector((state: RootState) => state.api.image_api);

  useEffect(() => {
    if (expanded !== undefined) {
      setIsExpanded(expanded);
    }
  }, [expanded]);

  const handleToggle = (): void => {
    if (expanded === undefined) {
      setIsExpanded((prev) => !prev);
    }
  };

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const files = Array.from(event.target.files || []);

    // Validate files
    const validFiles = files.filter((file) => {
      if (!validateFileSize(file, maxFileSize)) {
        alert(`ไฟล์ ${file.name} มีขนาดใหญ่เกินไป (สูงสุด ${maxFileSize}MB)`);
        return false;
      }
      return true;
    });

    const newFiles: UploadedFile[] = validFiles.map(
      (file: File, index: number) => ({
        id: Date.now() + index,
        name: file.name,
        size: formatFileSize(file.size),
        uploadDate: new Date().toLocaleDateString("th-TH"),
        file: file,
      })
    );

    setUploadedFiles([...uploadedFiles, ...newFiles]);

    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleDeleteFile = (fileId: number): void => {
    setUploadedFiles(uploadedFiles.filter((file) => file.id !== fileId));
  };

  const handleViewFile = (
    file: UploadedFile & { file_path?: string; file_name?: string }
  ): void => {
    console.log(file,'file')
    if (file.file) {
      // File object exists (locally uploaded)
      const url = URL.createObjectURL(file.file);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else if (file.file_path && file.name) {
      const fullUrl = `${base_url}${file.file_path}${file.name}`;
      window.open(fullUrl, "_blank");
    } else {
      alert("ไม่สามารถดูไฟล์ได้"); // "Unable to view file"
    }
  };

  const renderLeftButton = (): React.ReactNode => {
    if (renderButtonLeft) {
      return renderButtonLeft;
    }

    return (
      <Tooltip title={hoverTitle || "เพิ่มไฟล์"}>
        {/* <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (onClickButtonLeft) {
              onClickButtonLeft();
            } else {
              handleUploadClick();
            }
          }}
          sx={{ color: colors.white }}
        >
          <Additem />
        </IconButton> */}
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (onClickButtonLeft) {
              onClickButtonLeft();
            } else {
              handleUploadClick();
            }
          }}
          variant="contained"
          color="success"
          sx={{ color: colors.white }} startIcon={<Additem />}>
          เพิ่ม
        </Button>
      </Tooltip>
    );
  };

  const renderRightButton = (): React.ReactNode => {
    if (renderButtonRight) {
      return renderButtonRight;
    }

    return (
      <Button
        variant="text"
        startIcon={<Additem />}
        onClick={(e) => {
          e.stopPropagation();
          if (onClickButtonRight) {
            onClickButtonRight();
          } else {
            handleUploadClick();
          }
        }}
        sx={{
          color: colors.white,
          textTransform: "none",
        }}
      >
        {titleButtonRight}
      </Button>
    );
  };

  return (
    <>
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes}
        multiple={multiple}
        onChange={handleFileUpload}
      />

      <Accordion defaultExpanded={defaultExpanded} expanded={isExpanded}>
        <AccordionSummary
          aria-controls="panel1-content"
          id="panel1-header"
          bgColor={colorHeader}
          onClick={handleToggle}
        >
          {!isView && enableButtonLeft && renderLeftButton()}

          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.white,
                marginX: 1,
                fontWeight: 500,
              }}
            >
              {titleName}
              {required && (
                <Typography
                  component="span"
                  sx={{ color: "error.main", ml: 0.5 }}
                >
                  *
                </Typography>
              )}
            </Typography>

            {subTitleName && (
              <Typography
                variant="body2"
                sx={{
                  color: colors.white,
                  opacity: 0.8,
                  ml: 1,
                }}
              >
                {subTitleName}
              </Typography>
            )}
          </Box>

          {enableButtonRight && (
            <Box sx={{ ml: "auto", mr: 2 }}>{renderRightButton()}</Box>
          )}
        </AccordionSummary>

        <AccordionDetails>
          <Box>
            {/* Header */}
            <FileListHeader elevation={0}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={1}>
                  <Typography variant="subtitle2" align="center">
                    ลำดับ
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="subtitle2">ชื่อไฟล์</Typography>
                </Grid>
                {/* <Grid item xs={2}>
                  <Typography variant="subtitle2" align="center">
                    ขนาดไฟล์
                  </Typography>
                </Grid> */}
                {/* <Grid item xs={2}>
                  <Typography variant="subtitle2" align="center">
                    วันที่อัพโหลด
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="subtitle2" align="center">
                    การดำเนินการ
                  </Typography>
                </Grid> */}
              </Grid>
            </FileListHeader>

            {/* File List */}
            {!isView && uploadedFiles.length === 0 ? (
              <EmptyState>
                <FileIcon sx={{ fontSize: 48, color: "grey.300", mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  ยังไม่มีไฟล์ที่อัพโหลด
                </Typography>
                {/* <Typography variant="body2" color="text.secondary">
                  คลิกปุ่ม "ไฟล์แนบ" เพื่ออัพโหลดไฟล์
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Additem />}
                  onClick={handleUploadClick}
                  sx={{ mt: 2 }}
                >
                  เลือกไฟล์
                </Button> */}
              </EmptyState>
            ) : (
              uploadedFiles.map((file, index) => (
                <Grid key={file.id} container spacing={2} alignItems="center">
                  <Grid item xs={1}>
                    <Typography variant="body2" align="center">
                      {index + 1}
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FileIcon color="primary" fontSize="small" />
                      <Tooltip title={file.name}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.name}
                        </Typography>
                      </Tooltip>

                      <Typography
                        sx={{
                          color: "grey",
                        }}
                        variant="subtitle2"
                        align="center"
                      >
                        {file.size}
                      </Typography>

                      <Typography
                        sx={{
                          color: "grey",
                        }}
                        variant="subtitle2"
                        align="center"
                      >
                        {file.uploadDate ? moment(file.uploadDate).format("DD/MM/YYYY") : ""}
                      </Typography>
                    </Box>
                  </Grid>
                  {/* <Grid item xs={2}>
                      <Typography variant="body2" align="center">
                        {file.size}
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography variant="body2" align="center">
                        {file.uploadDate}
                      </Typography>
                    </Grid> */}
                  <Grid item xs={2}>
                    <OptionsButtonCustom
                      enableView
                      enableDelete={!isView}
                      onDeleteClick={() => {
                        handleDeleteFile(file.id);
                      }}
                      onViewClick={() => {
                        handleViewFile(file);
                      }}
                    />
                  </Grid>

                  {/* devider dash */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        borderTop: "1px dashed #DDE1EB",
                      }}
                    />
                  </Grid>
                </Grid>
              ))
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default AccordionUpload;
